import { Check } from "@phosphor-icons/react"
import { Badge } from "~/components/design-system/components"
import { evaluatePermission } from "~/hooks/usePermission"
import type { Permissions } from "~/server/api/utils/roles"
import type { SearchCommandsRegistration } from "~/stores/search-commands-store"
import { KeyboardShortcut } from "./KeyboardShortcut"
import type { DrillContext, PageCommand, SearchItem } from "./types"
import { matchesNameOrKeywords } from "./utils"

export const PAGE_COMMANDS_SOURCE_ID = "page-commands"

/** Commands of every registration not hidden behind a FullPageDialog, in mount order */
export const getVisibleCommands = (
  registrations: Record<string, SearchCommandsRegistration>,
): PageCommand[] =>
  Object.values(registrations).flatMap((registration) =>
    registration.covered ? [] : registration.commands,
  )

/** The command at `path` (ids from the top level down) */
export const findCommand = (
  commands: readonly PageCommand[],
  path: readonly string[],
): PageCommand | undefined => {
  const [id, ...rest] = path
  const command = commands.find((c) => c.id === id)
  if (!command || rest.length === 0) return command
  return command.subCommands && findCommand(command.subCommands, rest)
}

export const commandMatches = (command: PageCommand, query: string) =>
  matchesNameOrKeywords(command, query)

export const getDisabledReason = (
  command: PageCommand,
  permissions: Permissions,
): string | undefined => {
  if (command.disabledReason) return command.disabledReason
  if (!command.requires) return undefined
  return evaluatePermission(permissions, command.requires).reason
}

/** Unsectioned first, then each section in the order it first appears */
export const sortBySection = (commands: readonly PageCommand[]): PageCommand[] => {
  const sections = [...new Set(["", ...commands.map((c) => c.section ?? "")])]
  return [...commands].sort(
    (a, b) => sections.indexOf(a.section ?? "") - sections.indexOf(b.section ?? ""),
  )
}

const commandTrailing = (command: PageCommand) => {
  if (command.shortcut) return <KeyboardShortcut keys={[...command.shortcut]} sequence />
  if (!command.badge && !command.checked) return undefined
  return (
    <span className="flex items-center gap-2">
      {command.badge && <Badge size="sm">{command.badge}</Badge>}
      {command.checked && <Check size={16} className="text-fg-muted flex-shrink-0" />}
    </span>
  )
}

/** The drill-down showing the sub-list of the command at `path` */
export const toPageCommandDrill = (
  command: PageCommand,
  path: readonly string[],
): DrillContext => ({
  type: "source",
  sourceId: PAGE_COMMANDS_SOURCE_ID,
  title: command.name,
  placeholder: command.placeholder,
  data: path,
  previousSearch: "",
})

/** That drill-down for the visible command at `path`, if it still has a sub-list */
export const findPageCommandDrill = (
  registrations: Record<string, SearchCommandsRegistration>,
  path: readonly string[],
): DrillContext | undefined => {
  const command = findCommand(getVisibleCommands(registrations), path)
  return command?.subCommands ? toPageCommandDrill(command, path) : undefined
}

export const toPageCommandItem = (
  command: PageCommand,
  { disabledReason, drill }: { disabledReason: string | undefined; drill: () => void },
): SearchItem => ({
  id: command.id,
  title: command.name,
  subtitle: command.section,
  leading: command.icon && <span className="flex-shrink-0">{command.icon}</span>,
  trailing: commandTrailing(command),
  disabled: !!disabledReason,
  disabledReason,
  ...(command.subCommands
    ? { onSelect: drill, onDrill: drill, keepOpen: true }
    : { onSelect: command.run, keepOpen: command.keepOpen }),
  result: { name: command.id, type: "page command" },
})
